import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@commonsku/design-system';
import { useIdentity } from '../../hooks';
import type { MessageFeedback } from '../../types/skubot';
import {
    fetchConversations,
    selectActiveConversation,
    selectMessagesForConversation,
    selectLoading,
    sendChatMessage,
    selectConversationsList,
    setActiveConversationId,
    fetchConversationMessages,
    selectMessageError,
    submitFeedback,
    updateConversationTitle,
    deleteConversation,
} from '../../redux/skubot';
import { ChatWelcomeScreen } from './ChatWelcomeScreen';
import { ChatMessages } from './ChatMessages';
import { ChatSidebar } from './ChatSidebar';
import { SubmitFeedbackDialog } from './SubmitFeedbackDialog';
import { ChatHeader } from './ChatHeader';

const defaultSuggestions = [
    "Who are my top 5 customers in {previous_year}?",
    "What were Q3 sales for {company_name}?",
    "Show all open orders for {company_name}?",
    "What was the average order size last year?",
    "Which client had the highest order volume?",
];

export default function ChatsHomePage() {
    const currentYear = new Date().getFullYear().toString();
    const previousYear = (parseInt(currentYear) - 1).toString();
    const dispatch = useDispatch();
    const identity = useIdentity();
    const isLoading = useSelector(selectLoading);
    const pageContentRef = useRef<HTMLDivElement>(null);
    const activeConversation = useSelector(selectActiveConversation);
    const messages = useSelector(selectMessagesForConversation);
    const conversations = useSelector(selectConversationsList);
    const messagesError = useSelector(selectMessageError);
    const [input, setInput] = useState("");
    const [feedbackDialog, setFeedbackDialog] = useState<{ open: boolean; messageId: string | null }>({
        open: false,
        messageId: null,
    });

    const handleSend = useCallback((message?: string) => {
        const inputMessage = message ?? input;
        if (!inputMessage) return;
        dispatch(sendChatMessage(inputMessage));
        setInput("");
    }, [dispatch, input]);

    const handleSelectConversation = useCallback((id: string) => {
        dispatch(setActiveConversationId(id));
        dispatch(fetchConversationMessages(id));
    }, [dispatch]);

    const handleFeedback = useCallback((messageId: string, feedback: MessageFeedback) => {
        if (feedback === 'positive') {
            dispatch(submitFeedback({ messageId, feedback: 'positive' }));
        } else {
            setFeedbackDialog({ open: true, messageId });
        }
    }, [dispatch]);

    const handleFeedbackSubmit = useCallback((comment: string) => {
        if (feedbackDialog.messageId) {
            dispatch(submitFeedback({ messageId: feedbackDialog.messageId, feedback: 'negative', comment }));
        }
        setFeedbackDialog({ open: false, messageId: null });
    }, [dispatch, feedbackDialog.messageId]);

    const handleRenameConversation = useCallback((conversationId: string, title: string) => {
        dispatch(updateConversationTitle({ conversationId, title }));
    }, [dispatch]);

    const handleDeleteConversation = useCallback((conversationId: string) => {
        dispatch(deleteConversation(conversationId));
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchConversations());
    }, [dispatch]);

    useEffect(() => {
        let observer: ResizeObserver | null = null;
        const headerContainer = document.querySelector(".header-container");
        if (headerContainer && headerContainer instanceof HTMLElement) {
            observer = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    if (entry.target instanceof HTMLElement) {
                        const headerHeight = entry.target.offsetHeight;
                        if (pageContentRef.current) {
                            pageContentRef.current.style.height = `calc(100dvh - ${headerHeight}px)`;
                        }
                    }
                }
            });
            observer.observe(headerContainer);
        }

        return () => {
            if (observer) {
                observer.disconnect();
            }
        };
    }, []);

    return (
        <div ref={pageContentRef} style={{
            display: 'flex',
            height: 'calc(100dvh - 88px)',
            overflow: 'hidden',
            background: '#fafafa',
            color: 'var(--chakra-colors-gray-950)',
        }}>
            <ChatSidebar
                conversations={conversations}
                activeId={activeConversation?.sessionId ?? null}
                onNewChat={() => dispatch(setActiveConversationId(null))}
                onSelectConversation={handleSelectConversation}
            />

            <Box display="flex" flex="1" minW={0} minH={0} h="full" flexDirection="column">
                {activeConversation ? (
                    <>
                        <ChatHeader
                            conversation={activeConversation}
                            onRenameConversation={handleRenameConversation}
                            onDeleteConversation={handleDeleteConversation}
                        />
                        <ChatMessages
                            messages={messages}
                            input={input}
                            onInputChange={(value) => setInput(value)}
                            onSend={handleSend}
                            onFeedback={handleFeedback}
                            isLoading={isLoading}
                            errorMessage={messagesError}
                        />
                    </>
                ) : (
                    <ChatWelcomeScreen
                        userName={`${identity.user_first_name} ${identity.user_last_name}`.trim()}
                        input={input}
                        onInputChange={(value) => setInput(value)}
                        onSend={handleSend}
                        suggestions={defaultSuggestions.map(
                            (suggestion) => suggestion
                                .replace('{company_name}', identity.company_name)
                                .replace('{previous_year}', previousYear)
                                .replace('{current_year}', currentYear)
                        )}
                    />
                )}
            </Box>

            <SubmitFeedbackDialog
                open={feedbackDialog.open}
                onOpenChange={(open) => setFeedbackDialog((prev) => ({ ...prev, open }))}
                onSubmit={handleFeedbackSubmit}
            />
        </div>
    );
}
